<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <ModuleShell :title="$t('views.selectedGoat.dag.listTitle')"
               :icon="List"
               :hide-action="true">
    <ValueListTable :data="dag"
                    :columns="columns"
                    :loading="false"
                    @refresh="refreshDag"
                    @row-click="openConfirm">
      <template #cell-score="{ item }">
        <div class="w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl mx-auto"
             :class="getScoreBgClass((item as DagEntry).score)"
             :style="getScoreBgStyle((item as DagEntry).score)">
          {{ (item as DagEntry).score }}
        </div>
      </template>

      <template #toolbar-actions>
        <button @click="showAddDialog = true"
                class="btn h-[42px] px-4 rounded-lg flex items-center gap-2 font-medium text-white shadow-sm transition-transform active:scale-95-white"
                :style="{
                  backgroundColor: 'var(--color-success)',
                  border: '1px solid var(--color-success)'
                }">
          <Plus class="w-5 h-5" />
          <span>{{ $t('common.buttons.add') }}</span>
        </button>
      </template>
    </ValueListTable>

    <Teleport to="body">
      <ConfirmActionModule v-if="showConfirm && selectedDag"
                           :message="$t(
                            'views.selectedGoat.dag.deleteAskMessage',
                            { date: new Date(selectedDag.measuredAt).toLocaleDateString('de-DE') }
                          )"
                           variant="danger"
                           :confirmMessage="$t('common.buttons.delete')"
                           @cancel="closeConfirm"
                           @confirm="deleteConfirmed" />
    </Teleport>

  </ModuleShell>

  <!-- Add DAG Dialog -->
  <ValueInput :show="showAddDialog"
              :title="goat?.eartag || ''"
              :subtitle="goat?.name || ''"
              :inputLabel="$t('views.session.dag.label')"
              :placeholder="$t('views.session.dag.placeholder')"
              :inputMode="'dag'"
              :goatId="goatId"
              :showDateInput="true"
              @save="handleDagSaved"
              @cancel="showAddDialog = false" />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import ModuleShell from '@/components/ui/layout/ModuleShell.vue'
import ValueInput from '@/components/ui/inputs/ValueInput.vue'
import { List, Plus } from 'lucide-vue-next'
import { useDagStore } from '@/stores/dag.store.js'
import ValueListTable from '@/components/ui/tables/ValueListTable.vue'
import { useI18n } from 'vue-i18n'
import { useGoatStore } from '@/stores/goat.store.js'
import ConfirmActionModule from '@/components/actions/ConfirmAction.vue'

const { t } = useI18n()

interface DagEntry {
  id: number
  score: number
  measuredAt: string
  goatId: number
}

const columns = [
  { key: 'measuredAt', label: t('common.valueTableList.dagDate'), type: 'date' },
  { key: 'score', label: t('common.valueTableList.dagValue'), center: true, type: 'number' },
]

const props = defineProps<{
  dag: DagEntry[]
  goatId: number
  refreshDag: () => Promise<void> | void
}>()

const dagStore = useDagStore()
const goatStore = useGoatStore()
const showAddDialog = ref(false)
const showConfirm = ref(false)
const selectedDag = ref<any>(null)

const goat = computed(() => {
  return goatStore.goats.find(g => g.id === props.goatId)
})

async function handleDagSaved({ score, date }) {
  const measuredAt = date
    ? new Date(date + 'T' + new Date().toTimeString().split(' ')[0]).toISOString()
    : new Date().toISOString()

  try {
    await dagStore.createDagCheck({
      goatId: props.goatId,
      score: score,
      measuredAt: measuredAt
    })
    showAddDialog.value = false
    await props.refreshDag()
  } catch (error) {
    console.error('Failed to add DAG check:', error)
  }
}

function openConfirm(dagRow) {
  selectedDag.value = dagRow
  showConfirm.value = true
}
function closeConfirm() {
  showConfirm.value = false
  selectedDag.value = null
}
async function deleteConfirmed() {
  if (!selectedDag.value) return

  try {
    await dagStore.deleteDagCheck(selectedDag.value.id)
    await props.refreshDag()
    closeConfirm()
  } catch (e) {
    console.error('Failed to delete DAG check:', e)
  }
}

function getScoreBgClass(score:number) {
  const classes:any = {
    1: 'border-2 border-gray-300 !text-gray-900',
    2: '!text-gray-900',
    3: '',
    4: '',
    5: ''
  }
  return classes[score] || ''
}


function getScoreBgStyle(score:number) {
  const styles:any = {
    1: 'background-color: #ffffff',
    2: 'background-color: #fedcca',
    3: 'background-color: #faa89e',
    4: 'background-color: #ff7963',
    5: 'background-color: #f94943'
  }
  return styles[score] || 'background-color: #6b7280'
}

</script>

<style scoped></style>
